import type { FragmentType } from '~/graphql/generated';
import type { Video, VideoContent } from '~/types/Learn/Video';

import { adminVideoLearningAssetFragment, videoDataFragment, videoLearningAssetFragment } from '~/graphql/documents/learn/videos';
import { useFragment } from '~/graphql/generated';

import { transformAdminContentFile, transformPublicFile } from './fileTransformers';

const toVideoContent = (data: FragmentType<typeof videoDataFragment> | null | undefined): VideoContent | null => {
  const video = useFragment(videoDataFragment, data);
  if (!video) {
    return null;
  }

  return {
    videoUrl: video.videoUrl ?? '',
    description: video.description,
    provider: video.provider ?? null,
    speakers: video.speakers.map(speaker => ({
      name: speaker.name,
      organisation: speaker.organisation ?? '',
      bio: speaker.bio ?? '',
      imageId: speaker.imageId ?? null,
      imageUrl: speaker.imageUrl ?? null,
      image: transformPublicFile(speaker.image),
    })),
  };
};

export const transformAdminVideoAsset = (data: FragmentType<typeof adminVideoLearningAssetFragment>): Video => {
  const asset = useFragment(adminVideoLearningAssetFragment, data);

  return {
    id: asset.id,
    name: asset.name,
    slug: asset.slug,
    publishedAt: asset.publishedAt ?? null,
    publishedContentId: asset.publishedContentId ?? null,
    draftContentId: asset.draftContentId ?? null,
    coverImage: transformAdminContentFile(asset.coverImage),
    partnerId: asset.partnerId ?? null,
    partner: asset.partner ?? null,
    meta: asset.meta ?? null,
    considerationAreas: asset.considerationAreas ?? [],
    publishedContent: asset.publishedContent ? { id: asset.publishedContent.id, data: toVideoContent(asset.publishedContent.videoData) } : null,
    draftContent: asset.draftContent ? { id: asset.draftContent.id, data: toVideoContent(asset.draftContent.videoData) } : null,
  };
};

export const transformVideoAsset = (data: FragmentType<typeof videoLearningAssetFragment>): Video => {
  const asset = useFragment(videoLearningAssetFragment, data);

  return {
    id: asset.id,
    name: asset.name,
    slug: asset.slug,
    publishedAt: asset.publishedAt ?? null,
    draftContentId: null,
    coverImage: transformPublicFile(asset.coverImage),
    sponsor: asset.partner ? { id: asset.partner.id, name: asset.partner.name, logoUrl: asset.partner.iconFile?.publicUrl ?? null } : null,
    meta: asset.meta ?? null,
    publishedContent: asset.publishedContent ? { id: asset.publishedContent.id, data: toVideoContent(asset.publishedContent.videoData) } : null,
  };
};
